/**
 * StageOptionsMenuContainer — the stage column header's "..." overflow menu
 * (SE-6489).
 *
 * The approved design (Figma `Column-Pipeline`, component `80:14448` inside
 * frame `80:14903`) puts every stage action behind a single right-aligned
 * overflow trigger. Before SE-6489 the header rendered a blue pencil and a red
 * trash icon inline, one mis-click away from destroying a stage while dragging
 * cards across the board, plus a standalone sort trigger the design does not
 * have.
 *
 * Nothing was dropped in that move: rename still starts the same inline title
 * edit, delete still calls the same handler, reorder routes into the Stage
 * editor built by SE-5488, and the five per-stage sort options (shipped
 * behaviour — they drive the real re-sort and gate SE-4685 rank drag-reorder)
 * became rows here, keeping the `sort-option-<key>` testIDs the e2e suite
 * depends on.
 *
 * Renders through the shared `DesktopDropdownMenu` molecule, exactly like the
 * board-level `BoardOptionsMenu` next to it, so the trigger and surface chrome
 * come from the design system rather than from new one-off values.
 * @module features/player-kanban/components/molecules/StageOptionsMenu/StageOptionsMenuContainer
 */

import {
  ArrowLeftRight,
  ArrowUpDown,
  Edit2,
  MoreHorizontal,
  Trash2,
} from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";

import { Icon } from "@/components/atoms/Icon";
import DesktopDropdownMenu, {
  type DesktopDropdownMenuItem,
} from "@/components/molecules/DesktopDropdownMenu";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

import { useStageEditorPanelState } from "../../../hooks/useStageEditorPanelState";

/** The per-stage sort keys. Mirrors `KanbanColumnContainer`'s `SortOption`. */
export type StageSortOption = "name" | "age" | "gpr" | "contract" | "rank";

/** Props for {@link StageOptionsMenuContainer}. */
export interface StageOptionsMenuProps {
  /** Whether the viewer may rename this stage (also gates reorder). */
  readonly canEdit?: boolean;
  /** Whether the viewer may delete this stage. */
  readonly canDelete?: boolean;
  /** Whether the stage holds no players — sort rows are hidden when it does. */
  readonly isEmpty?: boolean;
  /** The pipeline this stage belongs to; required to open the Stage editor. */
  readonly boardId?: string;
  /** Currently active sort key. */
  readonly sortBy: StageSortOption;
  /** Currently active sort direction. */
  readonly sortDirection: "asc" | "desc";
  /** Starts the inline stage-title edit (what the pencil icon used to do). */
  readonly onRename: () => void;
  /** Applies a sort option to this stage's cards. */
  readonly onSort: (option: StageSortOption) => void;
  /** Deletes the stage (what the trash icon used to do). */
  readonly onDelete?: () => void;
}

/** Directional suffixes for the active sort row's label, by key. */
const DIRECTION_LABELS: Readonly<
  Record<Exclude<StageSortOption, "rank">, readonly [string, string]>
> = {
  name: ["(A-Z)", "(Z-A)"],
  age: ["(Young → Old)", "(Old → Young)"],
  gpr: ["(Low → High)", "(High → Low)"],
  contract: ["(Low → High)", "(High → Low)"],
};

/**
 * Builds one sort row's visible label, appending the direction only for the
 * active key (matching the labels the standalone sort menu used to show).
 * @param key - The sort key this row applies.
 * @param base - The human-readable name of the key.
 * @param sortBy - The currently active sort key.
 * @param sortDirection - The currently active direction.
 * @returns The row label.
 */
const sortLabel = (
  key: StageSortOption,
  base: string,
  sortBy: StageSortOption,
  sortDirection: "asc" | "desc"
): string => {
  if (key === "rank" || key !== sortBy) return `Sort by ${base}`;
  const [asc, desc] = DIRECTION_LABELS[key];
  return `Sort by ${base} ${sortDirection === "asc" ? asc : desc}`;
};

/** The sort rows, in the order the standalone menu listed them. */
const SORT_KEYS: ReadonlyArray<readonly [StageSortOption, string]> = [
  ["rank", "Rank"],
  ["name", "Name"],
  ["age", "Age"],
  ["gpr", "GPR"],
  ["contract", "Contract"],
];

/**
 * Stage column header overflow menu.
 * @param props - {@link StageOptionsMenuProps}
 * @param props.canEdit - Whether rename/reorder are offered.
 * @param props.canDelete - Whether delete is offered.
 * @param props.isEmpty - Whether the stage is empty (hides the sort rows).
 * @param props.boardId - Pipeline id, required to open the Stage editor.
 * @param props.sortBy - Active sort key.
 * @param props.sortDirection - Active sort direction.
 * @param props.onRename - Starts the inline title edit.
 * @param props.onSort - Applies a sort option.
 * @param props.onDelete - Deletes the stage.
 * @returns The overflow menu, or null when it would hold no rows.
 */
const StageOptionsMenuContainer = ({
  canEdit = false,
  canDelete = false,
  isEmpty = false,
  boardId,
  sortBy,
  sortDirection,
  onRename,
  onSort,
  onDelete,
}: StageOptionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => setIsOpen(prev => !prev), []);
  const onClose = useCallback(() => setIsOpen(false), []);

  const { open: openStageEditor } = useStageEditorPanelState();

  const handleReorder = useCallback(() => {
    if (boardId) openStageEditor(boardId);
  }, [boardId, openStageEditor]);

  const handleSortRank = useCallback(() => onSort("rank"), [onSort]);
  const handleSortName = useCallback(() => onSort("name"), [onSort]);
  const handleSortAge = useCallback(() => onSort("age"), [onSort]);
  const handleSortGpr = useCallback(() => onSort("gpr"), [onSort]);
  const handleSortContract = useCallback(() => onSort("contract"), [onSort]);

  const sortHandlers = useMemo<Record<StageSortOption, () => void>>(
    () => ({
      rank: handleSortRank,
      name: handleSortName,
      age: handleSortAge,
      gpr: handleSortGpr,
      contract: handleSortContract,
    }),
    [
      handleSortRank,
      handleSortName,
      handleSortAge,
      handleSortGpr,
      handleSortContract,
    ]
  );

  const items = useMemo<DesktopDropdownMenuItem[]>(() => {
    // Stage management first, then sort, then the destructive row last.
    const renameRows: DesktopDropdownMenuItem[] = canEdit
      ? [
          {
            id: "rename-stage",
            label: "Rename Stage",
            icon: Edit2,
            onPress: onRename,
          },
        ]
      : [];

    const reorderRows: DesktopDropdownMenuItem[] =
      canEdit && boardId
        ? [
            {
              id: "reorder-stages",
              /* SE-6858/SE-6924 — third icon for this row, chosen by pixel
                 checks at the menu's 16px icon frame. `Columns3`'s framed grid
                 rasterised as a grey block (SE-6858); its replacement `Kanban`
                 rendered crisp but its three detached top-hung bars READ as a
                 glyph missing strokes next to `Edit2`/`ArrowUpDown`/`Trash2` —
                 QA reported it as broken twice (SE-6924). `ArrowLeftRight`
                 says the action (stages reorder horizontally), joins the same
                 arrow family as the sort rows, and has no bar geometry left to
                 misread. Do not bring back a bars/columns glyph at this size. */
              label: "Reorder Stages",
              icon: ArrowLeftRight,
              onPress: handleReorder,
            },
          ]
        : [];

    const sortRows: DesktopDropdownMenuItem[] = isEmpty
      ? []
      : SORT_KEYS.map(([key, base]) => ({
          id: `sort-${key}`,
          label: sortLabel(key, base, sortBy, sortDirection),
          icon: ArrowUpDown,
          onPress: sortHandlers[key],
          testID: `sort-option-${key}`,
        }));

    const deleteRows: DesktopDropdownMenuItem[] = canDelete
      ? [
          {
            id: "delete-stage",
            label: "Delete Stage",
            icon: Trash2,
            onPress: onDelete ?? onClose,
            destructive: true,
            disabled: !onDelete,
          },
        ]
      : [];

    return [...renameRows, ...reorderRows, ...sortRows, ...deleteRows];
  }, [
    canEdit,
    canDelete,
    isEmpty,
    boardId,
    sortBy,
    sortDirection,
    sortHandlers,
    onRename,
    onDelete,
    onClose,
    handleReorder,
  ]);

  if (items.length === 0) return null;

  return (
    <DesktopDropdownMenu
      isOpen={isOpen}
      onClose={onClose}
      onToggle={onToggle}
      triggerTestID={TEST_ID_PLAYER_KANBAN.COLUMN_OPTIONS_MENU}
      triggerAccessibilityLabel="Stage options"
      /* 32x32 hit area, matching the design's IconButton: the 16px Icon is
         16px, so 8px of padding on each side gets there. */
      triggerClassName="rounded-full p-2"
      triggerContent={
        <Icon
          as={MoreHorizontal}
          size="sm"
          className="text-content-secondary"
        />
      }
      items={items}
    />
  );
};

StageOptionsMenuContainer.displayName = "StageOptionsMenuContainer";

export default StageOptionsMenuContainer;
